import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { configService } from '@/shared/lib/configService';

import { globalLogout } from '@/shared/utils/logout';
import {
  selectApiKey,
  selectIsAuthInitialized,
} from '@/shared/store/slices/authSlice';
import type { RootState } from '@/shared/store';

// In-memory storage for x-trace-id (for request tracing)
let traceIdStore: string | null = null;

// Marks APIs that are accessible without authentication
// Used to skip auto-login redirect for public eSIM flows
const PUBLIC_ENDPOINTS = [
  '/api/enterprises/verify-email',
  '/api/sim_profile/esim/send-activation',
  '/api/auth/user/login',
  '/api/auth/user/login/otp',
  '/api/auth/user/login/method',
  '/api/auth/user/resend-otp',
  '/api/auth/user/me', // Don't auto-logout on 401 during auth initialization
];

const isPublicEndpoint = (args: string | FetchArgs) => {
  const url = typeof args === 'string' ? args : args.url;
  return PUBLIC_ENDPOINTS.some((endpoint) => url.startsWith(endpoint));
};

/**
 * Custom base query with interceptor-like functionality
 * Handles auth tokens (via HttpOnly cookies), error responses, and automatic redirects
 */
const baseQueryWithInterceptors: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Read skipTraceId flag from Redux state
  const state = api.getState() as RootState;
  const skipTraceId = state?.apiConfig?.skipTraceId === true;
  const envTypeFromStorage =
    globalThis.localStorage?.getItem('envType') || 'production';

  // const url = typeof args === 'string' ? args : args.url;
  // console.log('[BaseApi] Request:', {
  //   url,
  //   method: typeof args === 'string' ? 'GET' : args.method,
  // });

  const baseQuery = fetchBaseQuery({
    baseUrl: configService.getApiHost(),
    //Include credentials (cookies) with all requests
    credentials: 'include',
    prepareHeaders: (headers) => {
      // Get apiKey from Redux auth state (fetched from /api/auth/user/me)
      const apiKey = selectApiKey(state);
      if (apiKey) {
        headers.set('x-api-key', apiKey);
      }
      headers.set('Content-Type', 'application/json');
      const lang = localStorage.getItem('i18nextLng') || 'en';
      headers.set('Accept-Language', lang);

      // Add envType header based on localStorage (use x-envType header)
      try {
        headers.set('x-envType', envTypeFromStorage);
      } catch {
        headers.set('x-envType', 'production');
      }

      // Disable HTTP caching to prevent 304 Not Modified responses
      headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
      headers.set('Pragma', 'no-cache');
      headers.set('Expires', '0');

      // Send stored x-trace-id for tracing (unless skipTraceId is true)
      if (traceIdStore && !skipTraceId) {
        headers.set('x-trace-id', traceIdStore);
      }

      return headers;
    },
  });

  const result = await baseQuery(args, api, extraOptions);

  // Store x-trace-id from response headers for tracing (in memory)
  if (result.meta?.response?.headers) {
    const traceId = result.meta.response.headers.get('x-trace-id');
    if (traceId) {
      traceIdStore = traceId;
    }
  }

  // Log response status
  // if (result.error) {
  // console.log('[BaseApi] Error response:', {
  //   url,
  //   status: result.error.status,
  // });
  // } else {
  // console.log('[BaseApi] Success response:', { url });
  // }

  // Error interceptor logic
  if (result.error) {
    const status = result.error.status;
    let isAbortLikeError = false;

    if (status === 'FETCH_ERROR') {
      const errorMessage = result.error.error || '';
      isAbortLikeError = /abort|aborted|cancel|canceled|cancelled/i.test(
        errorMessage
      );
    }

    // Request cancellation is expected during env switches/navigation resets.
    // Avoid showing noisy network toasts for these intentional aborts.
    if (isAbortLikeError) {
      return result;
    }

    // Handle 401 - Unauthorized (Token expired or invalid)
    // Note: With proactive token refresh (every 10 minutes), this should rarely happen
    // during active usage. If we get 401:
    // 1. Refresh failed multiple times, OR
    // 2. User is truly idle and refresh stopped, OR
    // 3. Refresh token expired (days old)
    // In all cases, logout is the correct response.
    if (status === 401) {
      // Skip auto-logout for public endpoints and auth initialization
      const isAuthInitialized = selectIsAuthInitialized(state);

      if (!isPublicEndpoint(args) && isAuthInitialized) {
        // console.log('[BaseApi] 401 received, logging out user');
        globalLogout('/login', true);
      }
      return result;
    }

    // Handle other errors
    if (status === 403) {
      // console.log('[BaseApi] 403 Forbidden - user lacks permission');
      // Stay logged in but show permission error to user
      return result;
    }
  }

  return result;
};
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithInterceptors,
  keepUnusedDataFor: 0, // ← Cache data for 0 seconds (immediate cleanup)
  // Define all tag types used across the app
  tagTypes: [
    'Auth',
    'User',
    'Enterprise',
    'ApiKeyManagement',
    'Menu',
    'Home',
    'Permissions',
    'Roles',
    'SimProfile',
    'AssetAllocationKpi',
    'CollectUserInput',
    'CollectUserChoice',
  ],
  // Start with empty endpoints - features will inject their own
  endpoints: () => ({}),
});

/**
 * Default subscription options for all RTK Query hooks
 * Apply these options to ensure consistent behavior across all API queries
 */
export const defaultSubscriptionOptions = {
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  refetchOnFocus: true,
};

/**
 * HOW TO USE skipTraceId FLAG:
 *
 * In your feature API endpoint definitions, pass skipTraceId in the query return object:
 *
 * Example 1: Use stored x-trace-id (default behavior)
 * builder.query({
 *   query: (id) => `/api/users/${id}`,
 *   // x-trace-id from previous response will be sent
 * })
 *
 * Example 2: Request a new x-trace-id from server
 * builder.query({
 *   query: (id) => ({
 *     url: `/api/users/${id}`,
 *     skipTraceId: true,  // ← Don't send stored trace ID, let server generate new one
 *   }),
 * })
 *
 * From components, call the hook with default subscription options:
 * const { data } = useGetUserQuery(userId, defaultSubscriptionOptions);  // Uses stored x-trace-id with auto-refetch
 * const { data } = useGetUserQuery(userId, defaultSubscriptionOptions);  // Applies refetchOnMountOrArgChange, refetchOnReconnect, refetchOnFocus
 */
